import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { createPortal, flushSync } from 'react-dom';

/** Сброс ghost DnD (сессия/проект в чат). Императивно: после flushSync в расписании dragend может не дойти до источника. */
let clearChatAttachDragState: (() => void) | null = null;

export function clearChatAttachDragGhost() {
  flushSync(() => {
    clearChatAttachDragState?.();
  });
}

export interface AttachedProject {
  id: string;
  name: string;
}

export interface AttachedSession {
  id: string;
  date: string;
  project_id?: string;
  label?: string;
}

export type ChatAttachDragPayload =
  | { type: 'project'; payload: AttachedProject }
  | { type: 'session'; payload: AttachedSession };

/** Единые отступ и режим позиции ghost при DnD в чат (проект и сессия) */
export const CHAT_ATTACH_DRAG_POSITION_MODE = 'top' as const;
export const CHAT_ATTACH_DRAG_OFFSET_Y_PX = 15;

export interface ChatAttachDragState {
  type: 'project' | 'session';
  payload: AttachedProject | AttachedSession;
  ghostEl: HTMLElement;
  position: { x: number; y: number };
  /** center = центр под курсором, top = верхний край под курсором */
  positionMode?: 'center' | 'top';
  /** Смещение по Y в px (для top — сдвиг вниз) */
  positionOffsetY?: number;
}

/**
 * Отдельный контекст только для setDragState — ссылка стабильна (useState),
 * строки таблицы и баблы не ре-рендерятся при старте/конце drag к чату.
 */
const ChatAttachDragSetterContext = createContext<
  Dispatch<SetStateAction<ChatAttachDragState | null>> | null
>(null);

/** 1x1 прозрачный элемент для скрытия нативного drag image */
function createTransparentDragImage(): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText = 'width:1px;height:1px;opacity:0;position:absolute;left:-9999px;pointer-events:none;';
  return el;
}

let transparentDragImageEl: HTMLElement | null = null;

export function getTransparentDragImage(): HTMLElement {
  if (!transparentDragImageEl) {
    transparentDragImageEl = createTransparentDragImage();
    document.body.appendChild(transparentDragImageEl);
  }
  return transparentDragImageEl;
}

export function ChatAttachDragProvider({ children }: { children: ReactNode }) {
  const [dragState, setDragState] = useState<ChatAttachDragState | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    clearChatAttachDragState = () => setDragState(null);
    return () => {
      clearChatAttachDragState = null;
    };
  }, []);

  useEffect(() => {
    if (!dragState) return;
    let rafId: number | null = null;
    let pendingX = dragState.position.x;
    let pendingY = dragState.position.y;

    const flush = () => {
      rafId = null;
      const el = containerRef.current;
      if (!el) return;
      el.style.left = `${pendingX}px`;
      el.style.top = `${pendingY}px`;
    };

    const onDragOver = (e: DragEvent) => {
      pendingX = e.clientX;
      pendingY = e.clientY;
      if (rafId == null) {
        rafId = requestAnimationFrame(flush);
      }
    };

    /** Глобально: источник мог размонтироваться (виртуализация) или dragend не дойти до строки */
    const forceClearGhost = () => {
      setDragState(null);
    };

    document.addEventListener('dragover', onDragOver, { capture: true });
    document.addEventListener('dragend', forceClearGhost, true);
    /** Не подписываемся на pointercancel: при старте нативного DnD браузер шлёт его на источник — ghost исчезал сразу */
    window.addEventListener('pointerup', forceClearGhost, true);
    return () => {
      document.removeEventListener('dragover', onDragOver, { capture: true });
      document.removeEventListener('dragend', forceClearGhost, true);
      window.removeEventListener('pointerup', forceClearGhost, true);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [dragState !== null]);

  useEffect(() => {
    if (!dragState) return;
    const container = containerRef.current;
    const ghost = dragState.ghostEl;
    if (container && ghost && !container.contains(ghost)) {
      container.appendChild(ghost);
    }
    return () => {
      if (ghost?.parentNode) ghost.parentNode.removeChild(ghost);
    };
  }, [dragState]);

  const offsetY = dragState?.positionOffsetY ?? 0;
  const transform =
    dragState?.positionMode === 'top'
      ? `translate(-50%, ${offsetY}px)`
      : 'translate(-50%, -50%)';

  const ghostPortal = dragState
    ? createPortal(
        <div
          ref={containerRef}
          className="chat-attach-drag-ghost"
          style={{
            position: 'fixed',
            left: dragState.position.x,
            top: dragState.position.y,
            transform,
            zIndex: 100000,
            pointerEvents: 'none',
          }}
        />,
        document.body
      )
    : null;

  return (
    <ChatAttachDragSetterContext.Provider value={setDragState}>
      {children}
      {ghostPortal}
    </ChatAttachDragSetterContext.Provider>
  );
}

/** Только для строк/сессий: стабильная ссылка, без ре-рендеров при drag */
export function useChatAttachDragSetter() {
  return useContext(ChatAttachDragSetterContext);
}
